import { Box, Button, TabList } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Facility, Notification } from '@fullstory/proto/fs/services/pinguin';
import { FilteredNotificationsList, FilterType } from '../FilteredNotificationsList';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  FullstoryActions,
  fullstorySelectors,
  NotificationsRequestActions,
  NotificationsRequests,
  useFetchAllNotifications,
  useFetchUnreadNotificationCount,
} from '@fullstory/redux';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';

const { getUserInfo } = fullstorySelectors;

export interface NotificationsListFiltersProps {
  orgId: string;
  notifications: Notification[];
  onClose?: () => void;
}

interface NotificationsFilters {
  label: string;
  id: FilterType;
  filter: FilterType;
  notifications: Notification[];
}

const filterFacilities: { [key in FilterType]: Facility[] } = {
  unread: [],
  Account: [Facility.ACCOUNT_MANAGEMENT],
  Alert: [Facility.METRIC_ALERTS, Facility.SEGMENT_ALERTS],
  Mentions: [Facility.NOTES, Facility.OPPORTUNITIES],
};

const filterNotifications = (notifications: Notification[], filter: FilterType): Notification[] =>
  notifications.filter(n =>
    n.configId?.facility ? filterFacilities[filter].includes(n.configId.facility) : false,
  );

export const NotificationsListFilters: React.FC<NotificationsListFiltersProps> = ({
  orgId,
  notifications,
  onClose,
}) => {
  const userInfo = useSelector(getUserInfo);
  const dispatch = useDispatch<AppDispatch>();
  const flags = useFlags();
  const totalUnreadNotifications = useFetchUnreadNotificationCount();
  const unreadCount = totalUnreadNotifications > 99 ? '99+' : totalUnreadNotifications;
  const notificationFilters: NotificationsFilters[] = useMemo(() => {
    const filters = [
      {
        label: intl.formatMessage(
          {
            defaultMessage: 'All unread ({unreadCount})',
            id: 'smT67x',
          },
          { unreadCount },
        ),
        id: FilterType.unread,
        filter: FilterType.unread,
        notifications: notifications.filter(n => !n.read),
      },
      {
        label: intl.formatMessage({ defaultMessage: 'Account', id: 'TwyMau' }),
        id: FilterType.account,
        filter: FilterType.account,
        notifications: filterNotifications(notifications, FilterType.account),
      },
      {
        label: intl.formatMessage({ defaultMessage: 'Alerts', id: '9zub+u' }),
        id: FilterType.alerts,
        filter: FilterType.alerts,
        notifications: filterNotifications(notifications, FilterType.alerts),
      },
      {
        label: userInfo.firstName
          ? intl.formatMessage(
              {
                defaultMessage: '@{name}',
                id: 'N9a3vq',
              },
              { name: userInfo.firstName },
            )
          : intl.formatMessage({ defaultMessage: 'Mentions', id: 'd7LCRi' }),
        id: FilterType.mentions,
        filter: FilterType.mentions,
        notifications: filterNotifications(notifications, FilterType.mentions),
      },
    ];

    if (!flags.hasBit(Flag.FeatAlerts)) {
      return filters.filter(f => f.id != FilterType.alerts);
    }

    return filters;
  }, [unreadCount, notifications, userInfo.firstName, flags]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = notificationFilters[selectedIndex];

  const { fetch, fetchMore } = useFetchAllNotifications();

  const onClickCta = (notification: Notification) => {
    if (!notification.read) {
      // Only mark as read if currently unread.
      if (notification.configId?.facility == Facility.SEGMENT_ALERTS) {
        // Update the segment alert last seen date from the createdAt date.
        const update = (Timestamp.toDate(notification?.createdAt) || new Date()).getTime();
        dispatch(NotificationsRequests.updateNotificationsSeen(update));
        dispatch(FullstoryActions.setNotificationsLastSeen(update));
      } else {
        dispatch(
          NotificationsRequestActions.markAsRead({
            orgId,
            notificationId: notification.id,
            read: true,
          }),
        ).then(() => fetch('', true));
      }
    }
    // Close the notification list on click.
    onClose?.();
  };
  const markAllAsRead = () => {
    if (!selected) {
      return;
    }
    dispatch(
      NotificationsRequestActions.markAllAsRead({
        orgId,
        // Note: For alerts, this will also "mark read" for SEGEMENT_ALERTS, which are never actually saved
        // on the backend. But that will not cause any problems, so this approach is simpler, and we may eventually
        // have them anyway.
        facilities: filterFacilities[selected.filter],
        read: true,
      }),
    ).then(() => fetch('', true));

    // If the user is requesting to mark all "Unread" or all "Alerts" as read, also reset the
    // segment alerts lastSeen date.
    if (selected?.filter == FilterType.alerts || selected?.filter == FilterType.unread) {
      const now = new Date().getTime();
      dispatch(NotificationsRequests.updateNotificationsSeen(now));
      dispatch(FullstoryActions.setNotificationsLastSeen(now));
    }
  };

  // if all notifications are read, there is nothing to mark as read
  const markReadDisabled = !selected?.notifications.some(n => !n.read);

  const controlRef = useRef<HTMLDivElement>(null);

  const notificationTitles = useMemo(
    () => notificationFilters.map(nf => nf.label),
    [notificationFilters],
  );

  return (
    <>
      <Box ref={controlRef} testId="notification-filters">
        <TabList onSelect={setSelectedIndex} selected={selectedIndex} titles={notificationTitles} />
        <Box margin={[12, 0]}>
          <Button
            size="x-small"
            variant="fill-builder"
            onClick={markAllAsRead}
            disabled={markReadDisabled}
          >
            <FormattedMessage defaultMessage="Mark all as read" id="/af+jQ" />
          </Button>
        </Box>
      </Box>

      {selected && (
        <FilteredNotificationsList
          filterType={selected.filter}
          notifications={selected.notifications}
          onClickCta={onClickCta}
          renderMore={fetchMore}
          heightOffset={controlRef.current?.clientHeight ?? 0}
        />
      )}
    </>
  );
};
